import ElementBound from '../core/ElementBound';
import Field from './Field';
import FileField from './FileField';
import FolderField from './FolderField';
import SelectField from './SelectField';
import CheckboxField from './CheckboxField';
import ChecklistField from './ChecklistField';
import ClockPickerField from './ClockPickerField';
import MultiInputField from './MultiInputField';
import AddressFieldGroup from './AddressFieldGroup';
import { parseDom } from 'larasnap';
import { DataType, FieldType } from 'larasnap/enums';
import isObject from 'lodash-es/isObject';

// Consider changing view mode fields to include a hidden field for storing value and then maintain a separare 'view' value

export default class extends ElementBound {
    static __larasnap_type = 'datamodel';

    _fields = [];
    _addressGroups = [];
    _visibilityConditions = [];

    /****************************************************************************************/
    //                                   Constructor
    /****************************************************************************************/

    constructor(element) {
        super(element);

        this._props = JSON.parse(element.val());

        element
            .context()
            .find(`[data-datamodel-name="${this._props.name}"]`)
            .forEach(el => this.createField(el));

        delete this._props.attributeProps;

        this.visibilityConditionsInit();
    }

    /****************************************************************************************/
    //                                      Relationships
    /****************************************************************************************/

    getRelation(name) {
        return this._props.relations[name];
    }

    getAttribute(name) {
        return this._props.attributes[name];
    }

    /****************************************************************************************/
    //                                      Fields
    /****************************************************************************************/

    createField(element) {
        const props =
            this._props.attributeProps[element.data('datamodel-field-name')];

        // We only want one field class instance for each field, even if there are multiple copies of the same field
        if (this.hasField(props.name)) {
            return;
        }

        props.displayType = element.data('datamodel-field-display-type');
        const cls =
            props.displayType !== 'hidden'
                ? this.getFieldClass(props.fieldType, props.dataType)
                : Field;

        //There could be multiple fields with the same name. This ensures that the element points to all of them
        element = element
            .context()
            .find(
                `.datamodel-field-wrapper[data-datamodel-name="${this._props.name}"][data-datamodel-field-name="${props.name}"]`,
            );
        const field = new cls(element, this, props);

        this._fields.push(field);

        if (props.addressGroup) {
            if (!this._addressGroups[props.addressGroup]) {
                this._addressGroups[props.addressGroup] = new AddressFieldGroup(
                    this,
                    props.addressGroup,
                );
            }
            this._addressGroups[props.addressGroup].addField(field);
        }
    }

    getFieldClass(fieldType, dataType) {
        if (fieldType === FieldType.FILE) {
            return FileField;
        } else if (fieldType === FieldType.FOLDER) {
            return FolderField;
        } else if (
            fieldType === FieldType.SELECT ||
            fieldType === FieldType.YES_NO
        ) {
            return SelectField;
        } else if (
            fieldType === FieldType.CHECKBOX ||
            fieldType === FieldType.SWITCH
        ) {
            return CheckboxField;
        } else if (fieldType === FieldType.CHECKLIST) {
            return ChecklistField;
        } else if (fieldType === FieldType.MULTI_INPUT) {
            return MultiInputField;
        } else if (
            dataType === DataType.TIME ||
            fieldType === FieldType.DURATION_PICKER
        ) {
            return ClockPickerField;
        } else {
            return Field;
        }
    }

    getField(name) {
        const field = this.findField(name);
        if (field) {
            return field;
        } else {
            console.error(`Error, field '${name}' does not exist`);
        }
    }

    getFields(...names) {
        if (names.length > 0) {
            return names.map(this.getField.bind(this));
        } else {
            return this._fields;
        }
    }

    findField(name) {
        return this._fields.find(x => x.getName() === name);
    }

    hasField(name) {
        return !!this.findField(name);
    }

    /****************************************************************************************/
    //                                    Get / Set
    /****************************************************************************************/

    get(name) {
        return this.getField(name).getValue();
    }

    set(field, value) {
        if (isObject(field)) {
            for (let i in field) {
                this.set(i, field[i]);
            }
            return;
        }

        return this.getField(field).setValue(value);
    }

    getValues() {
        return Object.fromEntries(
            this._fields.map(field => [field.getName(), field.getValue()]),
        );
    }

    /****************************************************************************************/
    //                                      Reload
    /****************************************************************************************/

    reload(fields = []) {
        if (fields.length === 0) {
            fields = this._fields.map(x => x.getName());
        }

        return this.panel()
            .$http('reload-model', this.getPrimaryKey())
            .setData({ fields })
            .send()
            .then(({ data: fields }) => this.reloadComplete(fields));
    }

    reloadComplete(fields) {
        Object.entries(fields).forEach(([key, { viewHtml, value }]) => {
            const field = this.getField(key);
            if (field.isView()) {
                field.setViewValue(viewHtml);
            }

            field.setInputValue(value);
        });

        parseDom();
    }

    /****************************************************************************************/
    //                               Visibility Conditions
    /****************************************************************************************/

    visibilityConditionsInit() {
        this.lockRowLabelWidths();

        const conditions = this._visibilityConditions;

        this._fields.forEach(field => {
            const visibilityCondition = this.getVisibilityCondition(field);
            if (visibilityCondition) {
                conditions.push(visibilityCondition);
            }
            const enabledCondition = this.getEnabledCondition(field);
            if (enabledCondition) {
                conditions.push(enabledCondition);
            }
        });

        this._fields.forEach(field => {
            if (field.hasInput()) {
                field.on(
                    'change',
                    this.checkAllVisibilityConditions.bind(this),
                );
            }
        });

        setTimeout(() => {
            this.checkAllVisibilityConditions();
        });
    }

    getVisibilityCondition(field) {
        let condition = field.getProps('visibleIf');
        let method = 'show';

        if (!condition) {
            condition = field.getProps('rowVisibleIf');
            method = 'showRow';
            if (!condition) {
                return null;
            }
        }

        if (
            condition.startsWith('function') &&
            !condition.startsWith('function:')
        ) {
            condition = eval(condition);
        }

        return { field, condition, method };
    }

    getEnabledCondition(field) {
        let condition = field.getProps('enabledIf');
        if (!condition) {
            return null;
        } else {
            if (
                condition.startsWith('function') &&
                !condition.startsWith('function:')
            ) {
                condition = eval(condition);
            }

            return {
                field,
                condition,
                method: 'enable',
            };
        }
    }

    checkAllVisibilityConditions() {
        const values = this.getValues();
        this._visibilityConditions.forEach(condition =>
            this.checkFieldVisibilityCondition(condition, values),
        );

        this.find('.datamodel-visible-if')?.forEach(el =>
            this.checkElementVisibilityCondition(el, values),
        );
    }

    checkFieldVisibilityCondition(condition, values) {
        const field = condition.field;
        const isVisible = this.evaluateVisibilityCondition(
            condition.condition,
            values,
        );

        if (!isVisible) {
            field.clearValue();
        }

        const wasMadeVisible = !field.getInput().is(':visible') && isVisible;

        field[condition.method](isVisible);

        if (wasMadeVisible && field.getProps('defaultValue') != undefined) {
            field.setValue(field.getProps('defaultValue'));
        }
    }

    checkElementVisibilityCondition(element, values) {
        const isVisible = this.evaluateVisibilityCondition(
            element.data('condition'),
            values,
        );

        if (isVisible) {
            element.show();
        } else {
            element.hide();
        }
    }

    /* eslint-disable no-unused-vars*/
    evaluateVisibilityCondition(statement, fields) {
        return eval(`${statement} ? true : false`);
    }

    /****************************************************************************************/
    //                                      Utilities
    /****************************************************************************************/

    prepareHttp() {
        const props = Object.fromEntries(
            this._fields.map(field => [field.getName(), field.getProps()]),
        );
        const name = this._props.name;
        const input = $(`input[name=datamodel-attribute-props-${name}]`);
        input.val(JSON.stringify(props));
    }

    lockRowLabelWidths() {
        this._fields
            .filter(field => field.hasRow())
            .map(field => field.getWrapper().find('.row-label'))
            .forEach(el => el.css('min-width', `${el.outerWidth()}px`));
    }

    exists() {
        return this._props.exists;
    }

    getPrimaryKey() {
        return this.get(this._props.primaryKey);
    }

    setPrimaryKey(val) {
        this.set(this._props.primaryKey, val);
    }

    find(selector) {
        return this.panel()?.find(selector);
    }

    getLabel() {
        return this._props.label;
    }

    trashed() {
        return !!this.get('deleted_at');
    }
}
